import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  AccordionDetails,
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { showLoader } from "../../../../Redux/Slices/loaderSlice";
import {
  app_get_role_access,
  app_update_role_access,
} from "../../../../Redux/Actions/action";
import ErrorHandler from "../../../../Utils/handler";
import { toast } from "react-toastify";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  borderRadius: "5px",
  overflow: "hidden",
  marginBottom: 5,
  border: "1px solid rgba(0, 0, 0, .125)",
  // boxShadow: 'none',
  "&:not(:last-child)": {
    // borderBottom: 0,
  },
  "&:before": {
    display: "none",
    marginBottom: 5,
  },
  "&.Mui-expanded": {
    marginBottom: 15,
    margin: "6px 0px",
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor: "#e6e6e6",
  borderBottom: "1px solid rgba(0, 0, 0, .125)",
  // color: "white",
  minHeight: 40,
  "&.Mui-expanded": {
    minHeight: 40,
  },
  "& .MuiAccordionSummary-content": {
    margin: "0px",
    "&.Mui-expanded": {
      margin: "0px",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme?.palette?.bgLightWhite?.main,
    fontSize: 15,
    textAlign: "center", // Horizontally align content
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "2px !important",
    textAlign: "center", // Horizontally align content
  },
  // "&:last-of-type": {
  //     borderBottom: "none", // Remove bottom border for the last cell
  // },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:last-of-type td, &:last-of-type th": {
    borderBottom: "none", // Remove bottom border for the entire last row
  },
}));

const AppRolePermission = () => {
  // ---------- Hook -----------
  const dispatch = useDispatch();
  const theme = useTheme();

  // --------------- State -------------
  const [data, setData] = useState();

  // --------------- API---------------
  const _getRoleList = async () => {
    dispatch(showLoader(true));
    let body = {
      // page: page,
      // limit: rowsPerPage
    };
    try {
      const result = await dispatch(app_get_role_access(body));
      const error = ErrorHandler(result);
      if (error) {
        setData(result?.payload?.data?.data);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const updatePageAccess = async (updatedPage) => {
    try {
      dispatch(showLoader(true));
      const body = {
        pageName: updatedPage?.pageName,
        actions: updatedPage?.actions,
      };
      const result = await dispatch(app_update_role_access(body)); // API call
      const error = ErrorHandler(result);

      if (error) {
        toast.success(
          result?.payload?.message || "Page data updated successfully"
        );
        await _getRoleList();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update page data");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const handleChange = (pageIndex, actionIndex, role) => {
    setData((prevData) => {
      const newData = [...prevData]; // Clone current state
      const page = newData[pageIndex]; // Get the specific page
      const action = page.actions[actionIndex]; // Get the specific action

      // Toggle the role value
      action.roles[role] = !action.roles[role];

      // Prepare payload to send the entire page's updated data
      const updatedPage = {
        pageName: page.pageName,
        actions: page.actions.map((action) => ({
          actionName: action.actionName,
          roles: action.roles,
        })),
      };

      // Make API call to update the page data
      updatePageAccess(updatedPage);

      return newData; // Update the state
    });
  };

  // ---------- useEffect ------------
  useEffect(() => {
    _getRoleList();
  }, []);

  return (
    <>
      <Box sx={{ padding: "0px 0px 18px 0px", borderRadius: "8px" }}>
        <Typography sx={{ fontSize: "20px", fontWeight: 500 }}>
          {" "}
          {"App Role & Permission"}
        </Typography>
      </Box>

      <Box my={2}>
        <Table>
          <TableHead
            sx={{
              backgroundColor: theme?.palette?.primary?.main,
              "& th": { color: "#fff" },
            }}
          >
            <TableRow sx={{ borderRadius: "10px", overflow: "hidden" }}>
              <StyledTableCell
                align="center"
                sx={{
                  maxWidth: "30px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  padding: "8px",
                }}
              >
                Page
              </StyledTableCell>
              {data?.length > 0 &&
                Object.keys(data?.[0]?.actions?.[0]?.roles)?.map((role) => {
                  if (role !== "SuperAdmin") {
                    return (
                      <StyledTableCell
                        align={"center"}
                        sx={{ maxWidth: "50px" }}
                        key={role}
                      >
                        {role}
                      </StyledTableCell>
                    );
                  }
                })}
            </TableRow>
          </TableHead>
        </Table>

        {data?.map((page, pageIndex) => (
          <Accordion key={page._id} elevation={0}>
            <AccordionSummary>
              <ExpandMoreIcon color="primary" />{" "}
              <Typography ml={1} fontSize={"15px"} color={"primary"}>
                {page?.pageName}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ padding: "0px 8px" }}>
              <Table>
                <TableBody>
                  {page?.actions?.map((action, actionIndex) => {
                    console.log(action.roles, "action");
                    const roleKeys = Object.keys(action.roles);

                    return (
                      <StyledTableRow key={action._id}>
                        <StyledTableCell sx={{ maxWidth: "30px" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "start",
                            }}
                          >
                            {action?.actionName}
                          </Typography>
                        </StyledTableCell>

                        {roleKeys?.map((role) => {
                          if (role !== "SuperAdmin") {
                            return (
                              <StyledTableCell key={role} align="center">
                                <Checkbox
                                  checked={action.roles[role] || false}
                                  onChange={() =>
                                    handleChange(pageIndex, actionIndex, role)
                                  }
                                />
                              </StyledTableCell>
                            );
                          }
                        })}
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </>
  );
};

export default AppRolePermission;
